
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { devLog, devError } from '@/utils/devLog';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/constants/queryKeys';
import { getCheckoutUrl } from '@/utils/routePaths';
import { clearPendingEnrollment } from '@/utils/pendingEnrollment';

export const useEnrollmentRedirect = (courseId?: string | null, price?: string | number, courseName?: string, username?: string, courseSlug?: string) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleEnrollAfterAuth = async () => {
    if (!courseId) {
      devLog("[EnrollmentRedirect] No course ID provided for enrollment");
      navigate("/dashboard/student");
      return;
    }

    try {
      devLog("[EnrollmentRedirect] Attempting enrollment after auth", {
        courseId,
        coursePrice: price,
        courseName,
        username,
        courseSlug,
        currentPath: window.location.pathname,
        timestamp: new Date().toISOString()
      });

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Authentication required");
      }

      // Check if already enrolled
      const { data: isEnrolled } = await supabase.rpc(
        'get_enrollment_status',
        { p_user_id: user.id, p_course_id: courseId }
      );
      
      if (isEnrolled) {
        devLog("[EnrollmentRedirect] User already enrolled", { courseId });
        clearPendingEnrollment();
        toast.info("You're already enrolled in this course!");
        navigate(`/dashboard/student/my-learning/course/${courseId}`, { replace: true });
        return;
      }

      // Handle free course enrollment with proper numeric conversion
      const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price || 0);

      if (!isNaN(numericPrice) && numericPrice <= 0) {
        devLog("[EnrollmentRedirect] Free course - enrolling immediately", {
          userId: user.id,
          courseId,
          username,
          courseSlug,
          numericPrice
        });
        
        const { error: enrollError } = await supabase
          .from('enrollments')
          .upsert({
            user_id: user.id,
            course_id: courseId,
            status: 'active'
          }, {
            onConflict: 'user_id,course_id'
          });

        if (enrollError) {
          devError("[EnrollmentRedirect] Error creating enrollment:", enrollError);
          throw new Error("Failed to enroll in course");
        }

        // Invalidate queries using the correct query key
        queryClient.invalidateQueries({ queryKey: QueryKeys.enrollments(user.id) });
        
        // Clear the pending enrollment
        clearPendingEnrollment();
        
        toast.success("Successfully enrolled in course!");
        
        // Navigate to dashboard with query params to trigger refresh
        navigate(`/dashboard/student/my-learning?from_free=true&course_id=${courseId}`, { replace: true });
        return;
      }

      // Handle paid course enrollment with proper checkout URL
      toast.info("Redirecting to payment...");
      
      // Get the proper checkout URL based on available parameters
      const checkoutUrl = getCheckoutUrl(username, courseSlug);
      
      devLog("[EnrollmentRedirect] Redirecting to checkout", { 
        checkoutUrl,
        username, 
        courseSlug,
        fullUrl: window.location.origin + checkoutUrl,
        currentPath: window.location.pathname
      });
      
      // Don't clear the enrollment yet - it will be handled after checkout
      navigate(checkoutUrl, {
        replace: true,
        state: { 
          courseId,
          courseName,
          coursePrice: price,
          username,
          courseSlug,
          from: username && courseSlug ? `/${username}/${courseSlug}` : window.location.pathname
        }
      });
    } catch (err: any) {
      devError("[EnrollmentRedirect] Enrollment error:", err);
      toast.error(err.message || "Failed to complete enrollment");
      clearPendingEnrollment();
      navigate("/dashboard/student", { replace: true });
    }
  };

  return { handleEnrollAfterAuth };
};
